import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { PdfToAudioDemoPage } from '@/components/astro/new-design-25/SEO/pdf-to-audio-demo-page';
import { pdfToAudioSearch } from '@/lib/pdf-to-audio-search';
import { hreflangLinks, seo } from '@/seo/seo';

const heroTitle = 'Portuguese PDF to audio';
const heroDescription =
  'Drop a Portuguese PDF and hear it read aloud with natural Brazilian or European Portuguese voices. Built for academic papers, ebooks, and Portuguese-language coursework.';

const featureFeatures = [
  {
    title: 'Brazilian and European Variants',
    description:
      'Switch between Brazilian Portuguese and European Portuguese voices to match the source and audience.',
  },
  {
    title: 'Nasal Vowels and Accents',
    description:
      'Portuguese nasal vowels (ã, õ, etc.) and accented vowels pronounce naturally, even when PDFs use non-standard fonts.',
  },
  {
    title: 'Academic Portuguese Vocabulary',
    description:
      'Handles Portuguese academic, literary, and journalistic content with proper register and technical terminology.',
  },
  {
    title: 'Right for Language Learners',
    description:
      'Slow the playback for shadowing, then speed up as your Portuguese ear improves on real native content.',
  },
];

const solutionBenefitsData = {
  title: 'Why Portuguese PDF readers stay with this tool',
  items: [
    {
      text: 'Natural Brazilian and European Portuguese voices, picked per project so the register fits',
      highlight: 'Brazilian and European Portuguese',
    },
    {
      text: 'Correct handling of nasal vowels, accented vowels, and special characters',
      highlight: 'nasal vowels and accented vowels',
    },
    {
      text: 'Reliable handling of Portuguese academic, literary, and journalistic terminology',
      highlight: 'academic, literary, and journalistic',
    },
    {
      text: 'Adjustable speed from 0.5x to 4x for shadowing and long-form listening',
      highlight: '0.5x to 4x',
    },
  ],
};

const useCasesData = {
  title: 'How Portuguese PDF to audio gets used',
  description:
    'From Brazilian university students to European Portuguese researchers and lusophone diaspora speakers.',
  useCases: [
    {
      title: 'Brazilian University Student',
      challenge:
        'Get through dense Portuguese course PDFs in fields like medicine, law, engineering, and social sciences.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Drop course PDFs and listen during commutes with natural Brazilian Portuguese, adjustable speed for tough sections.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Robotic Portuguese on academic vocabulary makes a 30-page reading sound like noise after a few pages.',
        },
      ],
    },
    {
      title: 'European Portuguese Researcher',
      challenge:
        'Triage Portuguese-language papers and produce audio versions of materials for European Portuguese audiences.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Pick a European Portuguese voice so course and research material sounds right to a Portuguese ear.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'One generic Brazilian-only voice that signals the content was not made for a European audience.',
        },
      ],
    },
    {
      title: 'Portuguese Learner Building Listening Skill',
      challenge:
        'Move past textbook audio and into real Portuguese PDFs at a pace you can actually follow.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Drop any Portuguese PDF, slow to 0.7x, follow synced highlighting in the Listening app.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Flat phrasing trains the wrong rhythm and slows down real comprehension later.',
        },
      ],
    },
    {
      title: 'Lusophone Diaspora Speaker',
      challenge:
        'Reconnect with Portuguese as an adult who grew up speaking it but does not read fluently anymore.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Hear natural Portuguese audio for any PDF, with synced highlighting to map sound to written form.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Wooden delivery actively makes the language sound foreign instead of familiar.',
        },
      ],
    },
  ],
};

const seoSections = [
  {
    title: 'Why Portuguese PDFs Need a Real Portuguese Voice',
    content:
      'Portuguese has features that generic PDF readers struggle with: nasal vowels, multiple variants (Brazilian and European Portuguese), elision of vowels at word boundaries, and a rich set of accented characters. A generic English-based PDF reader strips out the accents, flattens the nasals, and reads everything in a flat tone. The result is unrecognizable as Portuguese.',
    contentSecondary:
      'Listening uses native Portuguese voices trained on academic and journalistic Portuguese content. Both Brazilian Portuguese (the voice you hear on Globo News) and European Portuguese (the voice you hear on RTP) are supported. Nasal vowels, accented characters, and natural Portuguese prosody all sound the way they should.',
  },
  {
    title: 'Portuguese PDF to Audio for Study, Research, and Daily Reading',
    content:
      'For lusophone students and researchers, listening turns commutes, walks, and household tasks into reading time. Drop any Portuguese PDF into the converter on this page and the audio is ready in seconds. Adjustable speed lets you skim or slow down on tough passages.',
    contentSecondary:
      'The Listening app extends the same voices across iOS, Android, and Chrome extension. For Portuguese learners outside lusophone countries, this is the easiest way to get real native Portuguese input from any text source — paired with synced highlighting, you can build both reading and listening fluency at the same time.',
  },
];

const faqs = [
  {
    question: 'Which Portuguese voices are available?',
    answer:
      'Listening offers natural Portuguese voices in male and female options, covering both Brazilian Portuguese and European Portuguese variants so audio matches the audience.',
  },
  {
    question: 'How are nasal vowels and accents handled?',
    answer:
      'Portuguese nasal vowels (ã, õ, etc.) and accented vowels pronounce naturally, with correct stress and the right rhythm for Brazilian or European Portuguese contexts.',
  },
  {
    question: 'Can I use this for academic Portuguese content?',
    answer:
      'Yes. The voices are trained on Portuguese academic, literary, and journalistic content, with proper handling of academic vocabulary and formal register.',
  },
  {
    question: 'What are the free preview limits?',
    answer:
      'The free preview is capped at 10 pages, 25,000 characters, and 25 MB per PDF. Sign up to convert full Portuguese documents with no caps.',
  },
];

export const Route = createFileRoute(
  '/_marketing/_seo/portuguese-pdf-to-audio',
)({
  validateSearch: pdfToAudioSearch,
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'Portuguese PDF to Audio — Listen to Portuguese PDFs with Native Voices | Listening',
        description:
          'Convert Portuguese PDFs to audio with natural Brazilian and European Portuguese AI voices. Built for academic content, language learning, and professional audio. Free, no sign-up.',
      }),
    ],
    links: [...hreflangLinks('/portuguese-pdf-to-audio')],
  }),
  component: () => (
    <PdfToAudioDemoPage
      converterHeading={heroTitle}
      converterSubtitle={heroDescription}
      faqDescription='Quick answers about converting Portuguese PDFs to audio.'
      faqTitle='Portuguese PDF to audio FAQ'
      faqs={faqs}
      featureBadge='Portuguese voices'
      featureFeatures={featureFeatures}
      featureTitle='the Portuguese PDF tool lusophone readers stay with:'
      preferredLang='pt'
      seoSections={seoSections}
      solutionBenefitsData={solutionBenefitsData}
      useCasesData={useCasesData}
    />
  ),
});
